import { useState } from 'react';
import { api } from '../lib/api';
import { celebrateMilestone } from '../lib/milestones';
import { toast } from 'sonner';
import { Sparkles, PartyPopper, Trophy, Target, FastForward, Loader2 } from 'lucide-react';

/**
 * MilestoneTester — dev-only panel embedded in the scoring-debug view.
 *
 * Two independent ways to verify the milestone pipeline WITHOUT waiting
 * real days:
 *
 * 1. **Instant preview** — client-only. Calls `celebrateMilestone` directly
 *    so you see the confetti + hear the cached voice line without touching
 *    Mongo. Great for visual/audio QA on any device.
 *
 * 2. **Simulate streak** — backend. POSTs `/api/debug/simulate-streak`
 *    which sets `child.streak = target-1` + `last_session_date = yesterday`.
 *    Complete the CURRENT reading session (one real chunk read is fine)
 *    and the session-save path advances streak → target, firing the real
 *    milestone in `response.milestones` — the exact same code path used in
 *    production.
 *
 * Guarded by the debug PIN (re-entered by the parent panel before this
 * mounts + re-verified inline by the backend endpoint).
 */
export default function MilestoneTester({ debugPin, childId, language = 'en', voiceGender = 'female' }) {
  const [busyStreak, setBusyStreak] = useState(false);
  const [target, setTarget] = useState(3);
  const streakOptions = [3, 5, 7, 10, 14, 21, 30, 60, 90];

  const preview = async (kind) => {
    await celebrateMilestone({ kind, language, voiceGender });
  };

  const simulateStreak = async () => {
    if (!childId) return toast.error('No active child');
    setBusyStreak(true);
    try {
      const { data } = await api.post('/debug/simulate-streak', {
        pin: debugPin,
        child_id: childId,
        target_streak: Number(target),
        days_ago: 1,
      });
      toast.success(`Streak primed → ${data.streak_now}. Complete this chunk to hit ${target}.`, { duration: 6000 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Simulate failed');
    } finally { setBusyStreak(false); }
  };

  return (
    <div data-testid="milestone-tester" className="mt-3 bg-[#0F1424] border border-[#3A2E5C] text-slate-100 rounded-2xl p-4">
      <div className="text-[#FFB84D] font-bold uppercase tracking-wider text-xs mb-3 flex items-center gap-2">
        <PartyPopper size={14}/> Milestone tester
      </div>

      <div className="text-[10px] text-slate-400 uppercase font-bold mb-1">1. Instant preview (client only)</div>
      <div className="flex gap-2 flex-wrap mb-4">
        <button data-testid="preview-milestone-streak" onClick={() => preview('streak')}
          className="text-xs font-bold px-3 py-1.5 rounded-full bg-[#FF6B1A] text-white inline-flex items-center gap-1">
          <Sparkles size={12}/> Streak
        </button>
        <button data-testid="preview-milestone-reward" onClick={() => preview('reward')}
          className="text-xs font-bold px-3 py-1.5 rounded-full bg-[#F4B740] text-[#2B2140] inline-flex items-center gap-1">
          <Trophy size={12}/> Reward
        </button>
        <button data-testid="preview-milestone-perfect" onClick={() => preview('perfect')}
          className="text-xs font-bold px-3 py-1.5 rounded-full bg-[#2EC4B6] text-white inline-flex items-center gap-1">
          <Target size={12}/> Perfect
        </button>
      </div>

      <div className="text-[10px] text-slate-400 uppercase font-bold mb-1">2. Simulate streak (end-to-end)</div>
      <div className="flex items-center gap-2 flex-wrap">
        <select data-testid="milestone-target-select" value={target} onChange={e => setTarget(Number(e.target.value))}
          className="text-xs font-bold px-3 py-1.5 rounded-full bg-slate-800 border border-slate-700 text-white">
          {streakOptions.map(v => <option key={v} value={v}>Target: {v} days</option>)}
        </select>
        <button data-testid="simulate-streak-btn" onClick={simulateStreak} disabled={busyStreak}
          className="text-xs font-bold px-3 py-1.5 rounded-full bg-[#5B8DEF] text-white inline-flex items-center gap-1 disabled:opacity-60">
          {busyStreak ? <Loader2 size={12} className="animate-spin"/> : <FastForward size={12}/>} Prime & read once
        </button>
      </div>
      <p className="text-[10px] text-slate-500 mt-2 leading-relaxed">
        Preview fires confetti + audio immediately (no state change). Simulate sets streak = target − 1 with yesterday's timestamp so the very next chunk you read pushes streak to the target and triggers the real milestone in the session response.
      </p>
    </div>
  );
}
